import { CodeBlock } from '@lumapps/wrex-code-block/components/blocks/CodeBlock';
import { CODE_BLOCK } from '@lumapps/wrex-code-block/constants';
import { Column } from '@lumapps/wrex-columns/components/blocks/Column';
import { Columns } from '@lumapps/wrex-columns/components/blocks/Columns';
import { COLUMN, COLUMNS } from '@lumapps/wrex-columns/constants';
import { EmbeddedMap } from '@lumapps/wrex-embedded-map/components/blocks/EmbeddedMap';
import { EMBEDDED_MAP } from '@lumapps/wrex-embedded-map/constants';
import { EmbeddedVideo } from '@lumapps/wrex-embedded-video/components/blocks/EmbeddedVideo';
import { EMBEDDED_VIDEO } from '@lumapps/wrex-embedded-video/constants';
import { AlignedImageWrapper } from '@lumapps/wrex-enhanced-image/components/blocks/AlignedImageWrapper';
import { ImageGroup } from '@lumapps/wrex-enhanced-image/components/blocks/ImageGroup';
import { ALIGNED_IMAGE_WRAPPER, IMAGE_FEATURES, IMAGE_GROUP } from '@lumapps/wrex-enhanced-image/constants';
import { File } from '@lumapps/wrex-file/components/blocks/File';
import { FILE } from '@lumapps/wrex-file/constants';
import { Iframe } from '@lumapps/wrex-iframe/components/blocks/Iframe';
import { IFRAME } from '@lumapps/wrex-iframe/constants';
import { LinkPreview } from '@lumapps/wrex-link-preview/components/blocks/LinkPreview';
import { LINK_PREVIEW } from '@lumapps/wrex-link-preview/constants';
import { Link } from '@lumapps/wrex-link/components/blocks/Link';
import { LINK } from '@lumapps/wrex-link/constants';
import { ListItem } from '@lumapps/wrex-list/components/blocks/ListItem';
import { OrderedList } from '@lumapps/wrex-list/components/blocks/OrderedList';
import { UnorderedList } from '@lumapps/wrex-list/components/blocks/UnorderedList';
import { LIST_FEATURES, LIST_ITEM, ORDERED_LIST, UNORDERED_LIST } from '@lumapps/wrex-list/constants';
import { PlayVideo } from '@lumapps/wrex-play-video/components/blocks/PlayVideo';
import { PLAY_VIDEO } from '@lumapps/wrex-play-video/constants';
import { QuoteBlock } from '@lumapps/wrex-quote/components/blocks/QuoteBlock';
import { QUOTE_BLOCK } from '@lumapps/wrex-quote/constants';
import { Table, TD, TH, TR } from '@lumapps/wrex-table/components/blocks';
import { T_D, T_H, T_R, TABLE } from '@lumapps/wrex-table/constants';
import { Divider } from '@lumapps/wrex-typography/components/blocks/Divider';
import { Heading } from '@lumapps/wrex-typography/components/blocks/Heading';
import { InlineCode } from '@lumapps/wrex-typography/components/blocks/InlineCode';
import { Bold, Colored, Italic, Strikethrough, Underline } from '@lumapps/wrex-typography/components/blocks/Mark';
import { Paragraph } from '@lumapps/wrex-typography/components/blocks/Paragraph';
import {
    BOLD,
    COLORED,
    DIVIDER,
    HEADING,
    INLINE_CODE,
    ITALIC,
    PARAGRAPH,
    STRIKETHROUGH,
    TYPOGRAPHY_FEATURES,
    UNDERLINE,
} from '@lumapps/wrex-typography/constants';
import { UserMention } from '@lumapps/wrex-user-mention/components/blocks/UserMention';
import { USER_MENTION } from '@lumapps/wrex-user-mention/constants';
import { WREX_CORE } from '@lumapps/wrex/keys';
import type { getHtmlPasteOptions } from '@lumapps/wrex/utils/getHtmlPasteOptions';

export const STRUCTURED_CONTENT_WREX_EDITOR_CLASSNAME = 'wrex-editor';

export const DEFAULT_STRUCTURED_CONTENT_PLACEHOLDER = WREX_CORE.EDITOR_WITH_MENTION_PLACEHOLDER;

/**
 * Single source of truth for the wrex elements supported by the structured content editor
 */
const STRUCTURED_CONTENT_ELEMENT_TYPES = [
    PARAGRAPH,
    HEADING,
    CODE_BLOCK,
    INLINE_CODE,
    DIVIDER,
    UNORDERED_LIST,
    ORDERED_LIST,
    LIST_ITEM,
    IMAGE_GROUP,
    ALIGNED_IMAGE_WRAPPER,
    USER_MENTION,
    LINK_PREVIEW,
    LINK,
    TABLE,
    T_R,
    T_D,
    T_H,
    QUOTE_BLOCK,
    IFRAME,
    EMBEDDED_MAP,
    EMBEDDED_VIDEO,
    PLAY_VIDEO,
    FILE,
    COLUMN,
    COLUMNS,
] as const;

/**
 * Single source of truth for the wrex marks supported by the structured content editor
 */
const STRUCTURED_CONTENT_MARK_TYPES = [BOLD, ITALIC, UNDERLINE, COLORED, STRIKETHROUGH] as const;

const STRUCTURED_CONTENT_PASTE_ELEMENT_TYPES = STRUCTURED_CONTENT_ELEMENT_TYPES.filter(
    (type) => type !== ALIGNED_IMAGE_WRAPPER,
);

/**
 * Exhaustive list of rendered elements in structured content.
 */
export const ELEMENTS = [
    Paragraph,
    Heading,
    Divider,
    CodeBlock,
    Link,
    LinkPreview,
    File,
    ImageGroup,
    AlignedImageWrapper,
    UnorderedList,
    OrderedList,
    ListItem,
    UserMention,
    InlineCode,
    QuoteBlock,
    Table,
    TR,
    TD,
    TH,
    EmbeddedMap,
    EmbeddedVideo,
    PlayVideo,
    Iframe,
    Columns,
    Column,
];

/**
 * Exhaustive list of rendered mark in structured content
 */
export const MARKS = [Bold, Italic, Underline, Colored, Strikethrough];

export const STRUCTURED_CONTENT_SERIALIZATION_ELEMENTS = STRUCTURED_CONTENT_ELEMENT_TYPES;
export const STRUCTURED_CONTENT_SERIALIZATION_MARKS = STRUCTURED_CONTENT_MARK_TYPES;

/** Allowed elements to be pasted from HTML to slate, in a structured content */
export const STRUCTURED_CONTENT_HTML_PASTE_OPTIONS: Parameters<typeof getHtmlPasteOptions>[0] = {
    elements: [...STRUCTURED_CONTENT_PASTE_ELEMENT_TYPES],
    marks: [...STRUCTURED_CONTENT_MARK_TYPES],
};

/**
 * Defines the structured content toolbar breakpoints
 */
export const STRUCTURED_CONTENT_TOOLBAR_BREAKPOINTS = {
    /** Warning message + Undo, Redo, Formatting options, More insert options */
    'too-small': 0,
    /** Undo, Redo, Formatting options, More insert options */
    'group-insert': 199,
    /** Undo, Redo, Formatting options, Link, Emoji, More insert options */
    'formatting-submenu': 235, // Using 235 instead of the too small 230 of the mockup
    /** Undo, Redo, BlockType selector, Bold, Italic, More options, Link, Emoji, More insert options */
    'no-color': 325, // Using 325 instead of the too small 300 of the mockup
    /** Undo, Redo, BlockType selector, Bold, Italic, Color, More options, Link, Emoji, More insert options */
    'no-image-align': 370, // Using 370 instead of the to small 350 of the mockup
    /** Undo, Redo, BlockType selector, Bold, Italic, Color, Align selector, More options, Link, Emoji, Image, More insert options */
    'no-video-list': 435,
    /** Undo, Redo, BlockType selector, Bold, Italic, Color, Align selector, Bulleted list, More options, Link, Emoji, Image, Video, More insert options */
    'no-insert-label': 500,
    /** Undo, Redo, BlockType selector, Bold, Italic, Color, Align selector, Bulleted list, More options, Link, Emoji, Image, Video, More insert options */
    'no-blocktype-label': 600,
    /** Undo, Redo, BlockType selector, Bold, Italic, Color, Align selector, Bulleted list, More options, Link, Emoji, Image, Video, More insert options */
    full: 700,
};

/** List of the plugins enabled features for the structured content editor */
export const structuredContentEditorEnabledFeatures = {
    typography: [
        TYPOGRAPHY_FEATURES.bold,
        TYPOGRAPHY_FEATURES.italic,
        TYPOGRAPHY_FEATURES.underline,
        TYPOGRAPHY_FEATURES.color,
        TYPOGRAPHY_FEATURES.strikethrough,
        TYPOGRAPHY_FEATURES.heading,
        TYPOGRAPHY_FEATURES.inlineCode,
        TYPOGRAPHY_FEATURES.alignment,
        TYPOGRAPHY_FEATURES.divider,
    ],
    list: [LIST_FEATURES.unorderedList, LIST_FEATURES.orderedList],
    enhancedImage: [
        IMAGE_FEATURES.alignment,
        IMAGE_FEATURES.width,
        IMAGE_FEATURES.imageGallery,
        IMAGE_FEATURES.generateWithAI,
        IMAGE_FEATURES.link,
    ],
};

export const STRUCTURED_CONTENT_COLUMNS_OPTIONS = {
    maxNumber: 4,
    allowedElements: [PARAGRAPH, HEADING, IMAGE_GROUP, DIVIDER, LINK_PREVIEW],
};

export const STRUCTURED_CONTENT_TOOLBAR_CLASSNAME = 'structured-content-toolbar';
